import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ExclamationTriangleIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';

const DepositRefundModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  deposit
}) => {
  const [refundAmount, setRefundAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const clinic = useSelector((state) => state.clinics.selectedClinic);
  const paymentMethods = clinic?.paymentMethods || [];

  const availableBalance = deposit?.depositBalance || 0;
  const totalDeposit = deposit?.amount || 0;

  const handleConfirm = async () => {
    if (!reason.trim()) {
      return; // Don't submit if no reason provided
    }

    if (!selectedPaymentMethod) {
      return; // Don't submit if no payment method selected
    }

    const refundAmountNum = parseFloat(refundAmount);
    if (isNaN(refundAmountNum) || refundAmountNum <= 0) {
      return; // Don't submit if invalid amount
    }

    if (refundAmountNum > availableBalance) {
      return; // Don't submit if amount exceeds available balance
    }

    setIsSubmitting(true);
    try {
      await onConfirm({
        refundAmount: refundAmountNum,
        paymentMethod: selectedPaymentMethod,
        reason: reason.trim()
      });
      handleClose();
    } catch (error) {
      console.error(`[Deposit Refund Modal] Error processing refund:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRefundAmount('');
    setSelectedPaymentMethod('');
    setReason('');
    setIsSubmitting(false);
    onClose();
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow empty string or valid decimal numbers
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setRefundAmount(value);
    }
  };

  const refundAmountNum = parseFloat(refundAmount) || 0;
  const isValidAmount = refundAmountNum > 0 && refundAmountNum <= availableBalance;

  const paymentMethodDisplay = paymentMethods.find(
    method => method.value === deposit?.paymentMethod
  )?.englishDescription || deposit?.paymentMethod || 'Unknown Method';

  return (
    <Dialog open={isOpen} onClose={handleClose} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-gray-900/50 transition-opacity" />

        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full max-w-lg my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="bg-white px-6 py-6">
            <div className="flex items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                <CurrencyDollarIcon className="h-6 w-6 text-orange-600" aria-hidden="true" />
              </div>
              <div className="ml-4 mt-0 text-left flex-1">
                <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                  Refund Deposit
                </Dialog.Title>
                <div className="mt-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Deposit Information</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-blue-700 font-medium">Total Deposit:</span>
                        <span className="ml-2 text-blue-900">${totalDeposit.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Available Balance:</span>
                        <span className="ml-2 text-blue-900">${availableBalance.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Original Method:</span>
                        <span className="ml-2 text-blue-900">{paymentMethodDisplay}</span>
                      </div>
                      <div>
                        <span className="text-blue-700 font-medium">Date Collected:</span>
                        <span className="ml-2 text-blue-900">
                          {deposit?.paymentDate ? new Date(deposit.paymentDate).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-4">
                    You can refund up to <strong>${availableBalance.toFixed(2)}</strong> from this deposit.
                    {availableBalance < totalDeposit && (
                      <span className="text-orange-600"> (${(totalDeposit - availableBalance).toFixed(2)} has already been used)</span>
                    )}
                  </p>

                  {/* Refund Amount Input */}
                  <div className="mb-4">
                    <label htmlFor="refund-amount" className="block text-sm font-medium text-gray-700 mb-2">
                      Refund Amount <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        id="refund-amount"
                        type="text"
                        value={refundAmount}
                        onChange={handleAmountChange}
                        className="block w-full pl-7 pr-20 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6 rounded-md"
                        placeholder="0.00"
                        disabled={isSubmitting}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center">
                        <button
                          type="button"
                          onClick={() => setRefundAmount(availableBalance.toString())}
                          className="mr-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          disabled={isSubmitting}
                        >
                          Max
                        </button>
                      </div>
                    </div>
                    {refundAmount && !isValidAmount && (
                      <p className="mt-1 text-sm text-red-600">
                        {refundAmountNum > availableBalance 
                          ? `Amount cannot exceed available balance of $${availableBalance.toFixed(2)}`
                          : 'Please enter a valid amount greater than $0.00'
                        }
                      </p>
                    )}
                  </div>

                  {/* Payment Method Selection */}
                  <div className="mb-4">
                    <label htmlFor="refund-method" className="block text-sm font-medium text-gray-700 mb-2">
                      Refund Method <span className="text-red-600">*</span>
                    </label>
                    <select
                      id="refund-method"
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select refund method...</option>
                      {paymentMethods.map((method) => (
                        <option key={method.value} value={method.value}>
                          {method.englishDescription}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Reason Input */}
                  <div className="mb-4">
                    <label htmlFor="refund-reason" className="block text-sm font-medium text-gray-700 mb-2">
                      Refund Reason <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      id="refund-reason"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                      placeholder="Please provide a reason for this deposit refund..."
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex gap-3 justify-end">
              <button
                type="button"
                className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleConfirm}
                disabled={!reason.trim() || !selectedPaymentMethod || !isValidAmount || isSubmitting}
              >
                {isSubmitting ? 'Processing...' : `Refund $${refundAmountNum.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default DepositRefundModal;